import React, { useState, useEffect } from 'react';
//import { Chart } from 'chart.js/auto';

import {
  Chart,
  LineController,
  LineElement,
  PointElement,
  LinearScale,
  TimeScale,
  Tooltip
} from 'chart.js';
import 'chartjs-adapter-luxon';
import { DateTime } from 'luxon';

// Register required Chart.js components
Chart.register(
  LineController,
  LineElement,
  PointElement,
  LinearScale,
  TimeScale,
  Tooltip
);

const TrendingCoins = ({ homeUrl }) => {
  const [trendingCoins, setTrendingCoins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchTrendingCoins = async () => {
      try {
        const response = await fetch('https://api.coingecko.com/api/v3/search/trending');
        if (!response.ok) {
          throw new Error('Failed to fetch trending coins');
        }
        
        const data = await response.json();
        const coins = data.coins.map(item => item.item);
        
        // Fetch additional market data for each coin
        const coinIds = coins.map(coin => coin.id).join(',');
        const marketResponse = await fetch(
          `https://api.coingecko.com/api/v3/coins/markets?vs_currency=usd&x_cg_demo_api_key=CG-1No1VQvqppSkcaEeZoDCZFYB&ids=${coinIds}&order=market_cap_desc&per_page=10&sparkline=true&price_change_percentage=24h`
        );
        
        if (!marketResponse.ok) {
          throw new Error('Failed to fetch market data');
        }
        
        const marketData = await marketResponse.json();
        
        // Combine trending data with market data
        const combinedData = coins.map(coin => {
          const marketInfo = marketData.find(m => m.id === coin.id) || {};
          return {
            ...coin,
            ...marketInfo,
            price: marketInfo.current_price,
            priceChange: marketInfo.price_change_percentage_24h
          };
        });
        
        setTrendingCoins(combinedData);
      } catch (err) {
        console.error("Error fetching trending coins:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchTrendingCoins();
  }, []);

  // Initialize sparkline charts
  useEffect(() => {
    if (trendingCoins.length > 0) {
      trendingCoins.forEach((coin, index) => {
        if (coin.sparkline_in_7d?.price) {
          const canvasId = `trending-sparkline-${coin.id}-${index}`;
          const ctx = document.getElementById(canvasId)?.getContext('2d');
          
          if (ctx && !Chart.getChart(canvasId)) {
            new Chart(ctx, {
              type: 'line',
              data: {
                labels: coin.sparkline_in_7d.price.map((_, i) => i),
                datasets: [{
                  data: coin.sparkline_in_7d.price,
                  borderColor: coin.priceChange >= 0 ? '#00C853' : '#FF3D00',
                  borderWidth: 1,
                  fill: false,
                  tension: 0.4,
                  pointRadius: 0
                }]
              },
              options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: { display: false },
                  tooltip: { enabled: false }
                },
                scales: {
                  x: { display: false },
                  y: { display: false }
                }
              }
            });
          }
        }
      });
    }
  }, [trendingCoins]);

  if (loading) return <div className="loading">Loading trending coins...</div>;
  if (error) return <div className="error">Error: {error}</div>;

  return (
    <div className="relatedcoin_mainsec trendingcoins_mainsec">
      <div className="titlebox">
        <h2>Trending Coins</h2>
      </div>
      
      <div className="relatedcoin_boxsec">
        {trendingCoins.slice(0, 10).map((coin, index) => (
          <div className="relatedcoin_box" key={`${coin.id}-${index}`}>
            <div className="imgbox">
              <img 
                alt={coin.name} 
                src={coin.large || coin.image || `${homeUrl}/assets/img/default-coin.png`} 
                onError={(e) => {
                  e.target.src = `${homeUrl}/assets/img/default-coin.png`;
                }}
              />
              
              <div className="starbox">
                <i className="far fa-star"></i>
              </div>
            </div>
            
            <div className="namebox">
              <h4>{coin.name}</h4>
            </div>
            
            <div className="pricupdown">
              <h2>
                ${coin.price?.toLocaleString('en-US', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 6
                }) || 'N/A'}
                <span className={coin.priceChange >= 0 ? "upcoin" : "downcoin"}>
                  <i className={`fas fa-sort-${coin.priceChange >= 0 ? "up" : "down"}`}></i>
                  {Math.abs(coin.priceChange || 0).toFixed(2)}%
                </span>
              </h2>
            </div>
            
            <div className="sparkline_img">
              {coin.sparkline_in_7d?.price ? (
                <canvas 
                  id={`trending-sparkline-${coin.id}-${index}`}
                  style={{ width: '100%', height: '80px' }}
                />
              ) : (
                <img 
                  alt="sparkline" 
                  src={`${homeUrl}/assets/img/sparkline.svg`} 
                />
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default TrendingCoins;